(ns hai.entity.links
  "Add-link flow shared by the entity surfaces, split in two so hosts can place
  the pieces on either side of a divider: `link-adder` (the \"Add link\" pill
  with its inline input) and `link-list` (the numbered URL list, same shape
  tasks use). The palette leans on the sticky family; the `kraft` class on
  the adder swaps the dashed form pill for the sticky's filled one."
  (:require
   [clojure.string :as str]
   [hai.ui.icons :as icons]
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]
   [url.core :as url]))

(def ^:private link-icon (icons/mui-icon :outlined :link))
(def ^:private close-icon (icons/mui-icon :outlined :close))

(o/defstyled link-list :div
  {:display "flex" :flex-direction "column"
   :gap "5px"
   :min-width "0"}
  [:.note-link-row
   {:display "flex" :align-items "center"
    :gap "8px"}]
  [:.note-link-num
   {:flex "0 0 auto"
    :min-width "12px"
    :text-align "right"
    :font-size "10.5px"
    :color t/--note-sage-80}]
  [:.note-link-anchor
   {:flex "0 1 auto" :min-width "0"
    :color t/--note-sage-80
    :font-family "ui-monospace, 'SF Mono', Menlo, monospace"
    :font-size "12px"
    :text-decoration "underline"
    :overflow "hidden" :text-overflow "ellipsis" :white-space "nowrap"}
   [:&:hover {:color t/--card-forest}]]
  [:.note-link-remove
   {:flex "0 0 auto"
    :width "18px" :height "18px"
    :padding "0"
    :border "none" :background "transparent"
    :border-radius "4px"
    :color t/--note-sage-80
    :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"}
   [:&:hover {:color t/--card-forest :background t/--note-kraft-10}]
   [:svg {:width "13px" :height "13px" :fill "currentColor"}]]
  ([{:keys [links on-remove]}]
   [:<>
    (for [[idx link-url] (map-indexed vector links)]
      ^{:key link-url}
      [:div.note-link-row
       [:span.note-link-num (str (inc idx))]
       [:a.note-link-anchor
        {:href link-url
         :target "_blank"
         :rel "noopener noreferrer"
         :title link-url
         ;; Don't take focus on click — inside a sticky, focus-within expands
         ;; the note and shifts the target out from under the cursor.
         :on-mouse-down #(.preventDefault %)
         :on-click #(.stopPropagation %)}
        (url/url->display-label link-url)]
       (when on-remove
         [:button.note-link-remove
          {:type "button"
           :aria-label "Remove link"
           :title "Remove link"
           :on-mouse-down #(.preventDefault %)
           :on-click (fn [e]
                       (.stopPropagation e)
                       (on-remove link-url))}
          [close-icon]])])]))

(o/defstyled link-adder :div
  {:display "flex"
   :min-width "0"}
  ;; Same pill the task form uses for optional extras (.quick-add-pill).
  [:.add-link-trigger
   {:display "inline-flex" :align-items "center" :gap "4px"
    :align-self "flex-start"
    :padding "5px 12px"
    :border-width "1px" :border-style "dashed" :border-color t/--property-3
    :border-radius "999px"
    :background "transparent"
    :font-family "inherit"
    :font-size "12px" :font-weight "500"
    :color t/--link-muted
    :cursor "pointer"
    :-webkit-tap-highlight-color "transparent"}
   [:&:hover {:color t/--card-forest :border-color t/--card-sage}]
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
  [:.link-input-row
   {:flex "1" :min-width "0"
    :display "flex" :align-items "center" :gap "6px"}]
  [:.link-input
   {:flex "1" :min-width "0"
    :padding "4px 8px"
    :border-width "1px" :border-style "solid"
    :border-color t/--note-sage-35
    :border-radius "6px"
    :background "rgba(255,255,255,0.6)"
    :font-family "inherit" :font-size "12px"
    :color t/--charcoal
    :outline "none"}
   [:&:focus {:border-color t/--note-sage-80}]
   [:&.invalid {:border-color t/--property-13}]
   [:&::placeholder {:color t/--note-sage-55}]]
  ;; Filled pill, like the buttons at the top of the stickies.
  [:.link-input-confirm
   {:flex-shrink "0"
    :white-space "nowrap"
    :padding "5px 12px"
    :border "none" :border-radius "999px"
    :background t/--note-kraft-10
    :font-family "inherit"
    :font-size "12px" :font-weight "500"
    :color t/--note-kraft
    :cursor "pointer"}
   [:&:hover {:background t/--note-kraft-18}]
   [:&:disabled {:opacity "0.5" :cursor "not-allowed"}]
   [:&:disabled:hover {:background t/--note-kraft-10}]]
  [:.link-input-cancel
   {:flex-shrink "0"
    :width "22px" :height "22px"
    :padding "0"
    :border "none" :background "transparent"
    :border-radius "99px"
    :color t/--note-sage-80
    :cursor "pointer"
    :display "inline-flex" :align-items "center" :justify-content "center"}
   [:&:hover {:color t/--card-forest :background t/--note-kraft-10}]
   [:svg {:width "14px" :height "14px" :fill "currentColor"}]]
  ;; On yellow paper the trigger joins the sticky's filled-pill family (the
  ;; + button, "Random note") instead of the form's dashed pill.
  [:&.kraft
   [:.add-link-trigger
    {:border "none"
     :background t/--note-kraft-10
     :color t/--note-kraft
     :padding "4px 10px"
     :font-size "11px"
     :font-weight "600"}
    [:&:hover {:background t/--note-kraft-18 :color t/--note-kraft}]]]
  ;; Roomier sizing to sit alongside full-size form fields (the note modal),
  ;; rather than the compact sticky default.
  [:&.comfortable
   [:.link-input
    {:padding "8px" :font-size "0.875rem" :border-radius "8px"}]
   [:.link-input-confirm
    {:padding "8px 14px" :font-size "0.875rem"}]
   [:.link-input-cancel
    {:width "26px" :height "26px"}
    [:svg {:width "16px" :height "16px"}]]]
  ;; `always-open?` skips the "Add link" pill and shows the paste form straight
  ;; away — for hosts (e.g. the note modal) that already gate it behind their own
  ;; toggle and don't want a pill nested inside a pill. In that mode a committed
  ;; link clears the field and keeps the form open, and ✕ / Escape call
  ;; `on-cancel` (the host collapses the section) instead of dropping to a pill.
  ;;
  ;; `render-entity-search` turns the field into a searchable dropdown:
  ;; a host-supplied render fn — mirrors `hai.entity.sections`' `render-tag-search`
  ;; — given `{:value :on-change :on-pick :on-enter-no-match :exclude-uuids
  ;; :invalid? :placeholder :auto-focus? :on-cancel}`, returns the surface's own
  ;; entity-search element (so its data layer, e.g. re-frame on the web-app,
  ;; stays local to the host). Typing searches contacts, notes and tasks; picking
  ;; one calls `on-pick-entity` with the entity map and clears the draft. Enter
  ;; with nothing highlighted (`on-enter-no-match`) falls through unchanged to the
  ;; existing URL commit. `linked-uuids` lists entities already attached (plus the
  ;; host note itself) to exclude from results.
  ([{:keys [class on-add on-cancel always-open? render-entity-search
            on-pick-entity linked-uuids]}]
   (r/with-let [!input-open? (r/atom false)
                !draft (r/atom "")
                !invalid? (r/atom false)
                close-input! (fn []
                               (reset! !input-open? false)
                               (reset! !draft "")
                               (reset! !invalid? false))
                cancel! (fn []
                          (if always-open?
                            (when on-cancel (on-cancel))
                            (close-input!)))]
     (let [commit! (fn []
                     ;; Blank commits cancel — the pill form collapses back to
                     ;; the pill, the always-open form closes its section.
                     (if (str/blank? @!draft)
                       (cancel!)
                       (if-let [link-url (url/normalize-http-url @!draft)]
                         (do (on-add link-url)
                             (if always-open?
                               (do (reset! !draft "") (reset! !invalid? false))
                               (close-input!)))
                         (reset! !invalid? true))))
           pick-entity! (fn [entity]
                          (on-pick-entity entity)
                          (reset! !draft "")
                          (reset! !invalid? false)
                          (when-not always-open? (close-input!)))]
       [:<>
        {:class class}
        (if (or always-open? @!input-open?)
          [:div.link-input-row
           (if render-entity-search
             (render-entity-search
              {:value @!draft
               :placeholder "Search or paste a link…"
               :auto-focus? true
               :invalid? @!invalid?
               :exclude-uuids linked-uuids
               :on-change (fn [v]
                            (reset! !draft v)
                            (reset! !invalid? false))
               :on-pick pick-entity!
               :on-enter-no-match commit!
               :on-cancel cancel!})
             [:input.link-input
              {:type "url"
               :class (when @!invalid? "invalid")
               :placeholder "Paste a link…"
               :value @!draft
               :auto-focus true
               :aria-label "Link URL"
               :aria-invalid @!invalid?
               :on-change (fn [e]
                            (reset! !draft (.. e -target -value))
                            (reset! !invalid? false))
               ;; Clicking off works as the Add button: commit what's typed,
               ;; cancel when blank. Add/✕ keep focus through their mousedown,
               ;; so they never read as leaving.
               :on-blur (fn [_] (commit!))
               :on-key-down (fn [e]
                              (case (.-key e)
                                "Enter" (do (.preventDefault e)
                                            (commit!))
                                ;; Stop Escape here or it also closes a host
                                ;; dialog (Radix listens on the document).
                                "Escape" (do (.stopPropagation e)
                                             (cancel!))
                                nil))}])
           [:button.link-input-confirm
            {:type "button"
             :disabled (str/blank? @!draft)
             ;; Keep focus in the URL input — no blur flicker, and inside a
             ;; sticky no focus-shift before the click lands.
             :on-mouse-down #(.preventDefault %)
             :on-click commit!}
            "Add"]
           [:button.link-input-cancel
            {:type "button"
             :aria-label "Cancel adding link"
             :title "Cancel"
             :on-mouse-down #(.preventDefault %)
             :on-click (fn [_] (cancel!))}
            [close-icon]]]
          [:button.add-link-trigger
           {:type "button"
            :on-mouse-down #(.preventDefault %)
            :on-click (fn [e]
                        (.stopPropagation e)
                        (reset! !input-open? true))}
           [link-icon]
           "Add link"])]))))

;; Revealed link editor for hosts that gate it behind their own toggle pill
;; (note + task modals, task composers): the always-open paste form plus the
;; list of added links — no nested "Add link" pill. `comfortable?` selects the
;; roomier sizing for full-size dialogs; omit it for compact inline composers.
;; The host owns the url collection; ✕ / Escape call `on-cancel`.
(o/defstyled links-section :div
  {:display "flex" :flex-direction "column" :gap "8px"}
  ([{:keys [urls on-add on-remove on-cancel comfortable?]}]
   [:<>
    [link-adder {:class (when comfortable? "comfortable")
                 :always-open? true
                 :on-add on-add
                 :on-cancel on-cancel}]
    (when (seq urls)
      [link-list {:links urls :on-remove on-remove}])]))
